import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import type { PaletteAction } from './types'

interface ShellContextValue {
  /** Registriert Aktionen für die Befehlspalette; gibt die Abmelde-Funktion zurück. */
  registerActions: (actions: PaletteAction[]) => () => void
  actions: PaletteAction[]
  openPalette: () => void
  closePalette: () => void
  paletteOpen: boolean
  setPaletteOpen: (o: boolean) => void
}

const ShellContext = createContext<ShellContextValue | null>(null)

export function ShellProvider({ children }: { children: React.ReactNode }) {
  const [paletteOpen, setPaletteOpen] = useState(false)
  const [registry, setRegistry] = useState<{ key: number; actions: PaletteAction[] }[]>([])
  const counter = useRef(0)

  const registerActions = useCallback((actions: PaletteAction[]) => {
    const key = ++counter.current
    setRegistry(r => [...r, { key, actions }])
    return () => setRegistry(r => r.filter(x => x.key !== key))
  }, [])

  const actions = useMemo(() => registry.reduce<PaletteAction[]>((acc, r) => acc.concat(r.actions), []), [registry])
  const value = useMemo<ShellContextValue>(() => ({
    registerActions, actions, paletteOpen, setPaletteOpen,
    openPalette: () => setPaletteOpen(true),
    closePalette: () => setPaletteOpen(false),
  }), [registerActions, actions, paletteOpen])

  return <ShellContext.Provider value={value}>{children}</ShellContext.Provider>
}

export function useShell() {
  const ctx = useContext(ShellContext)
  if (!ctx) throw new Error('useShell/useShellActions nur innerhalb der AppShell verwenden')
  return ctx
}

/**
 * Seiten registrieren damit Aktionen in der Befehlspalette (Strg+K), solange sie angezeigt werden:
 *   useShellActions([{ id: 'neu', label: 'Neuen Einsatz anlegen', run: () => setOpen(true) }], [])
 * `run` wird immer in der jeweils aktuellen Fassung aufgerufen (keine veralteten Closures).
 */
export function useShellActions(actions: PaletteAction[], deps: React.DependencyList = []) {
  const { registerActions } = useShell()
  const ref = useRef(actions)
  ref.current = actions
  useEffect(() => {
    const proxies = ref.current.map((a, i) => ({ ...a, run: () => ref.current[i]?.run() }))
    return registerActions(proxies)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registerActions, ...deps])
}

/** Befehlspalette programmatisch öffnen. */
export function useCommandPalette() {
  const { openPalette, closePalette, paletteOpen } = useShell()
  return { open: openPalette, close: closePalette, isOpen: paletteOpen }
}
